// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { DetailedAsset } from '@/asset/api/types';
import { DepreciationChart } from '@/asset/components/depreciation-chart';
import {
  CHART_HEIGHT_PX,
  DepreciationExit,
  getRemainingLifeSummary,
  haveSameInstant,
  haveSamePeriods,
  sanitizeFilePart,
} from '@/asset/components/depreciation-chart-utilities';
import { DepreciationScheduleTable } from '@/asset/components/depreciation-schedule-table';
import { exportDepreciationScheduleCSV } from '@/asset/utilities/export-depreciation-schedule-csv';
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import {
  BarChartOutlined,
  DownloadOutlined,
  TableChartOutlined,
} from '@/constants/hardfin-standard-icons';
import { getExceptionConfig } from '@/report/utilities/exception-config';
import {
  Alert,
  AlertTitle,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import { memo, useRef, useState } from 'react';

interface Props {
  asset: DetailedAsset;
  currentDate: DateTime;
}

type DepreciationScheduleView = 'chart' | 'table';

const DEPRECIATION_SCHEDULE_VIEW_OPTIONS: FilterOption<DepreciationScheduleView>[] =
  [
    {
      icon: <BarChartOutlined fontSize="small" />,
      label: 'Chart',
      value: 'chart',
    },
    {
      icon: <TableChartOutlined fontSize="small" />,
      label: 'Table',
      value: 'table',
    },
  ];

function DepreciationChartSectionComponent(props: Props) {
  const theme = useTheme();
  const depreciationPeriods = props.asset.accounting.depreciationPeriods;
  const assetExceptions = props.asset.accounting.assetExceptions;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [viewMode, setViewMode] = useState<DepreciationScheduleView>('chart');

  if (assetExceptions.length > 0) {
    return (
      <Box
        data-testid="depreciation-chart-warning"
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          maxWidth: '100%',
          minWidth: 0,
          mt: 4,
          overflow: 'hidden',
          pt: 3,
          width: '100%',
        }}
      >
        <Typography mb={2} variant="h6">
          Depreciation schedule
        </Typography>
        <Alert severity="warning">
          <AlertTitle>Depreciation schedule unavailable</AlertTitle>
          <Typography variant="body2">
            Resolve the following asset exceptions before viewing the
            depreciation schedule
          </Typography>
          <List
            component="ol"
            dense
            disablePadding
            sx={{
              listStyleType: 'decimal',
              mt: 1,
              pl: 3,
            }}
          >
            {assetExceptions.map((exceptionType) => {
              const config = getExceptionConfig(exceptionType);

              return (
                <ListItem
                  component="li"
                  disableGutters
                  key={exceptionType}
                  sx={{
                    alignItems: 'flex-start',
                    display: 'list-item',
                    py: 0.5,
                  }}
                >
                  <ListItemText
                    primary={config.label}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      variant: 'body2',
                    }}
                    secondary={config.guidance}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Alert>
      </Box>
    );
  }

  if (depreciationPeriods == null) {
    return null;
  }

  const inventoryDate = props.asset.inInventoryDate.startOf('day');
  const inServiceDate =
    props.asset.accounting.inServiceDate?.startOf('day') ?? null;
  const usefulLifeMonths = props.asset.accounting.usefulLife;
  const currentDate = props.currentDate;
  const scrappedDate =
    props.asset.scrappedContext?.disposalDate.startOf('day') ?? null;
  const soldDate = props.asset.accounting.firstSaleDate?.startOf('day') ?? null;
  let exit: DepreciationExit | null = null;
  // Depreciation ends at the first sale or disposal, even after a buyback.
  if (
    scrappedDate !== null &&
    (soldDate === null || scrappedDate.valueOf() <= soldDate.valueOf())
  ) {
    exit = { date: scrappedDate, kind: 'Disposed' };
  } else if (soldDate !== null) {
    exit = { date: soldDate, kind: 'Sold' };
  }

  const exitDate = exit?.date ?? null;
  const disposalDate =
    scrappedDate ??
    props.asset.ownership?.ownershipChangeDate?.startOf('day') ??
    null;
  const endOfUsefulLife =
    inServiceDate !== null && usefulLifeMonths !== null
      ? inServiceDate.plus({ months: usefulLifeMonths }).minus({ days: 1 })
      : null;
  const chartEndDate =
    disposalDate ??
    (endOfUsefulLife !== null &&
    endOfUsefulLife.valueOf() > currentDate.valueOf()
      ? endOfUsefulLife
      : currentDate);
  const acquisitionCost =
    props.asset.accounting.acquisitionCogs?.amount.toNumber() ?? null;
  const capitalizedCost =
    props.asset.accounting.capitalizedCosts?.amount.toNumber() ?? null;
  const salvageValue =
    props.asset.accounting.salvageValue?.amount.toNumber() ?? null;
  // Required on the asset, so it is read directly. Passing undefined here would land on
  // formatCurrency's hardcoded USD default and render a GBP asset in dollars.
  const currencyCode = props.asset.accounting.costCurrencyCode;
  const remainingLifeSummary = getRemainingLifeSummary({
    currentDate,
    endOfUsefulLife,
    exit,
    inServiceDate,
    usefulLifeMonths,
  });
  const exportLabel =
    viewMode === 'chart'
      ? 'Export depreciation chart image'
      : 'Export depreciation schedule CSV';

  return (
    <Box
      data-testid="depreciation-chart-section"
      sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        maxWidth: '100%',
        minWidth: 0,
        mt: 4,
        overflow: 'hidden',
        pt: 3,
        width: '100%',
      }}
    >
      <Stack
        alignItems={{ sm: 'center', xs: 'flex-start' }}
        direction={{ sm: 'row', xs: 'column' }}
        justifyContent="space-between"
        mb={2}
        spacing={2}
      >
        <Typography variant="h6">Depreciation schedule</Typography>
        <Stack
          alignItems={{ sm: 'center', xs: 'stretch' }}
          direction={{ sm: 'row', xs: 'column' }}
          spacing={1}
          width={{ sm: 'auto', xs: '100%' }}
        >
          <FilterToggleGroup
            ariaLabel="Depreciation schedule view"
            onChange={(value) => setViewMode(value as DepreciationScheduleView)}
            options={DEPRECIATION_SCHEDULE_VIEW_OPTIONS}
            value={viewMode}
          />
          <Tooltip arrow title={exportLabel}>
            <IconButton
              aria-label={exportLabel}
              onClick={() => {
                if (viewMode === 'chart') {
                  exportDepreciationChart({
                    assetSerial: props.asset.serial,
                    chartTitle: 'Depreciation schedule',
                    svgRef,
                    theme,
                  });
                  return;
                }

                exportDepreciationScheduleCSV({
                  acquisitionCost,
                  assetSerial: props.asset.serial,
                  capitalizedCost,
                  endOfUsefulLife,
                  exitDate,
                  inServiceDate,
                  currencyCode,
                  periods: depreciationPeriods,
                  salvageValue,
                  usefulLifeMonths,
                });
              }}
              size="small"
            >
              <DownloadOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {remainingLifeSummary !== null && (
        <Typography color="text.secondary" mb={2} variant="body2">
          {remainingLifeSummary}
        </Typography>
      )}
      {viewMode === 'chart' ? (
        <DepreciationChart
          acquisitionCost={acquisitionCost}
          capitalizedCost={capitalizedCost}
          chartEndDate={chartEndDate}
          currentDate={currentDate}
          disposalDate={disposalDate}
          endOfUsefulLife={endOfUsefulLife}
          inServiceDate={inServiceDate}
          inventoryDate={inventoryDate}
          currencyCode={currencyCode}
          periods={depreciationPeriods}
          salvageValue={salvageValue}
          svgRef={svgRef}
          usefulLifeMonths={usefulLifeMonths}
        />
      ) : (
        <DepreciationScheduleTable
          acquisitionCost={acquisitionCost}
          capitalizedCost={capitalizedCost}
          endOfUsefulLife={endOfUsefulLife}
          exitDate={exitDate}
          inServiceDate={inServiceDate}
          currencyCode={currencyCode}
          periods={depreciationPeriods}
          salvageValue={salvageValue}
          usefulLifeMonths={usefulLifeMonths}
        />
      )}
    </Box>
  );
}

// Compare by value: the asset reference changes every refetch, so memoize on the fields this section reads
export const DepreciationChartSection = memo(
  DepreciationChartSectionComponent,
  areDepreciationChartSectionPropsEqual
);

function areDepreciationChartSectionPropsEqual(
  prev: Props,
  next: Props
): boolean {
  const prevAccounting = prev.asset.accounting;
  const nextAccounting = next.asset.accounting;
  return (
    haveSameInstant(prev.currentDate, next.currentDate) &&
    prev.asset.serial === next.asset.serial &&
    prevAccounting.usefulLife === nextAccounting.usefulLife &&
    prevAccounting.acquisitionCogs === nextAccounting.acquisitionCogs &&
    prevAccounting.capitalizedCosts === nextAccounting.capitalizedCosts &&
    prevAccounting.salvageValue === nextAccounting.salvageValue &&
    // A currency change re-denominates without converting, so every amount above compares
    // equal. Without this the chart, tooltip and table keep the previous symbol.
    prevAccounting.costCurrencyCode === nextAccounting.costCurrencyCode &&
    haveSameInstant(prev.asset.inInventoryDate, next.asset.inInventoryDate) &&
    haveSameInstant(
      prevAccounting.inServiceDate,
      nextAccounting.inServiceDate
    ) &&
    haveSameInstant(
      prevAccounting.firstSaleDate,
      nextAccounting.firstSaleDate
    ) &&
    haveSameInstant(
      prev.asset.scrappedContext?.disposalDate ?? null,
      next.asset.scrappedContext?.disposalDate ?? null
    ) &&
    haveSameInstant(
      prev.asset.ownership?.ownershipChangeDate ?? null,
      next.asset.ownership?.ownershipChangeDate ?? null
    ) &&
    haveSameExceptions(
      prevAccounting.assetExceptions,
      nextAccounting.assetExceptions
    ) &&
    haveSamePeriods(
      prevAccounting.depreciationPeriods,
      nextAccounting.depreciationPeriods
    )
  );
}

function haveSameExceptions(
  prev: DetailedAsset['accounting']['assetExceptions'],
  next: DetailedAsset['accounting']['assetExceptions']
): boolean {
  if (prev === next) {
    return true;
  }
  if (prev.length !== next.length) {
    return false;
  }
  return prev.every((exceptionType, index) => exceptionType === next[index]);
}

export function DepreciationChartSectionSkeleton() {
  return (
    <Box data-testid="depreciation-chart-skeleton" sx={{ mt: 4 }}>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h6">Depreciation schedule</Typography>
        <Skeleton height={32} variant="circular" width={32} />
      </Stack>
      <Skeleton height={CHART_HEIGHT_PX} variant="rounded" width="100%" />
    </Box>
  );
}

function exportDepreciationChart(props: {
  assetSerial: string;
  chartTitle: string;
  svgRef: { current: SVGSVGElement | null };
  theme: Theme;
}) {
  void (async () => {
    if (props.svgRef.current === null) {
      return;
    }

    try {
      const serializer = new XMLSerializer();
      const svgMarkup = serializer.serializeToString(props.svgRef.current);
      const svgBlob = new Blob([svgMarkup], {
        type: 'image/svg+xml;charset=utf-8',
      });
      const svgUrl = URL.createObjectURL(svgBlob);
      const image = new Image();
      const svgWidth =
        props.svgRef.current.viewBox.baseVal.width ||
        props.svgRef.current.width.baseVal.value;
      const svgHeight =
        props.svgRef.current.viewBox.baseVal.height ||
        props.svgRef.current.height.baseVal.value;
      const exportWidthPx = 1100;
      const exportPaddingPx = 24;
      const exportScale = 1.5;
      const titleBaselinePx = 34;
      const subtitleBaselinePx = 58;
      const chartTopPx = 84;
      const scaledChartHeightPx =
        ((exportWidthPx - exportPaddingPx * 2) / svgWidth) * svgHeight;
      const canvas = document.createElement('canvas');

      canvas.width = Math.ceil(exportWidthPx * exportScale);
      canvas.height = Math.ceil(
        (scaledChartHeightPx + chartTopPx + exportPaddingPx) * exportScale
      );
      canvas.style.width = `${String(exportWidthPx)}px`;
      canvas.style.height = `${String(
        Math.ceil(scaledChartHeightPx + chartTopPx + exportPaddingPx)
      )}px`;

      const context = canvas.getContext('2d');
      if (context === null) {
        throw new Error('Could not create canvas context');
      }

      context.scale(exportScale, exportScale);

      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('Could not load chart image'));
        image.src = svgUrl;
      });

      context.fillStyle = '#ffffff';
      context.fillRect(
        0,
        0,
        canvas.width / exportScale,
        canvas.height / exportScale
      );
      context.fillStyle = props.theme.palette.text.primary;
      context.font = '600 28px sans-serif';
      context.fillText(props.chartTitle, exportPaddingPx, titleBaselinePx);
      context.fillStyle = props.theme.palette.text.secondary;
      context.font = '400 16px sans-serif';
      context.fillText(
        `Asset serial ${props.assetSerial}`,
        exportPaddingPx,
        subtitleBaselinePx
      );
      context.drawImage(
        image,
        exportPaddingPx,
        chartTopPx,
        exportWidthPx - exportPaddingPx * 2,
        scaledChartHeightPx
      );

      const pngBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });
      if (pngBlob === null) {
        throw new Error('Could not export chart image');
      }

      const downloadUrl = URL.createObjectURL(pngBlob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = `depreciation-chart-${sanitizeFilePart(
        props.assetSerial
      )}-${DateTime.now().toFormat('yyyyLLdd')}.png`;
      anchor.click();

      URL.revokeObjectURL(svgUrl);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      Sentry.captureException(error);
    }
  })();
}
